import { CropFreeOutlined } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Grid,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/styles';
import { capitalCase } from 'change-case';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetListDataQuery, useLoadChurchIdPdfQuery } from '../api/apiSlice';
import Iconify from '../components/fragments/Iconify';
import Screen from '../components/fragments/Screen';
import CardHome from '../components/legacy/CardHome';
import { profileActions } from '../store/local/contents';
import { setUser } from '../store/slices/authSlice';
import downloadChurchId from '../utils/downloadChurchId';
import { isDevMode } from '../utils/environment';

export default function ProfileTab() {
    const navigate = useNavigate();
    const theme = useTheme();
    const state = useSelector((st) => st);

    const { user: currentUser } = state.auth;
    const { churches } = state.list2;

    const { data: encodedId, isFetching } = useLoadChurchIdPdfQuery(
        { list: '/church_id', params: { preventAdblock: true } },
        { refetchOnMountOrArgChange: true }
    );

    const { data: userData } = useGetListDataQuery(
        { list: '/user' },
        { refetchOnMountOrArgChange: true }
    );

    userData?.data && setUser(userData?.data);

    const {
        firstName,
        surname,
        username,
        gender,
        church,
        role,
        links,
        status: { baptised, salvation } = {}, //TODO
        contact: { phoneNumber, email } = {},
    } = currentUser;

    const userChurch = churches?.find(({ id }) => id === church?.id);

    return (
        <Screen tab disableAppBar disablePadding>
            <Box
                sx={{
                    height: (window.innerWidth * 312) / 820,
                    width: '100%',
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    backgroundImage: `url("${userChurch?.avatar}")`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundBlendMode: 'soft-light',
                }}
            ></Box>
            <Box
                sx={{
                    width: window.innerWidth - 32,
                    position: 'absolute',
                    top: window.innerWidth * 0.2695,
                    left: 16,
                }}
                className="d-flex justify-content-between"
            >
                <Avatar
                    sx={{
                        width: 80,
                        height: 80,
                        border: `3px solid ${theme.palette.background.default}`,
                    }}
                    src={links?.avatar}
                    onClick={() => navigate('/change-avatar')}
                >
                    {`${firstName?.slice(0, 1)}${surname?.slice(0, 1)}`}
                </Avatar>
                <Box className="d-flex align-items-end">
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate('/update-profile')}
                    >
                        Update Profile
                    </Button>
                </Box>
            </Box>
            <Grid item sx={{ mt: 6, mx: 2 }} xs={12}>
                <Paper sx={{ background: 'none', boxShadow: 'none', mb: 2 }}>
                    <Typography variant="h6">{`${firstName} ${surname}`}</Typography>
                    {username && (
                        <Typography color="text.secondary" variant="body2">
                            @{username}
                        </Typography>
                    )}
                </Paper>
                <Grid container spacing={1}>
                    <ListInfo xs={12} icon="bxs:church" label={church?.name} />
                    <ListInfo
                        xs={5}
                        icon="ph:gender-intersex"
                        label={capitalCase(gender || '')}
                    />
                    <ListInfo
                        xs={7}
                        icon="carbon:phone-ip"
                        label={phoneNumber}
                    />
                    <ListInfo
                        xs={5}
                        icon="akar-icons:heart"
                        label={salvation === 'saved' ? 'Saved' : 'Not Saved'}
                    />
                    <ListInfo
                        xs={7}
                        icon="akar-icons:water"
                        label={baptised === 'yes' ? 'Baptized' : 'Not Baptized'}
                    />
                    {email && (
                        <ListInfo
                            xs={12}
                            icon="eva:email-outline"
                            label={
                                email?.length > 22
                                    ? email?.slice(0, 22) + '...'
                                    : email
                            }
                        />
                    )}
                </Grid>
            </Grid>
            <div id="canvas"></div>
            <Grid sx={{ mb: 2, mx: 2 }} xs={12} item>
                <Button
                    variant="contained"
                    disabled={isFetching}
                    onClick={() => downloadChurchId(encodedId, currentUser)}
                >
                    Download Church ID
                </Button>
            </Grid>

            <Grid sx={{ mx: 2 }} container spacing={1}>
                {role === 'usher' && (
                    <CardHome
                        icon={<CropFreeOutlined color="primary" />}
                        link={'/scan-qr'}
                        full={true}
                        desc={null}
                        title={'Scan QR Code'}
                    />
                )}

                {profileActions.map(({ icon, name, link, full, desc }) => {
                    return (
                        <CardHome
                            key={`${name}`}
                            icon={icon}
                            title={name}
                            link={link}
                            full={full}
                            desc={desc}
                        />
                    );
                })}
                {(phoneNumber == '799055101' || isDevMode) && (
                    <CardHome
                        icon={null}
                        title={'Debug'}
                        link={'/debug'}
                        full={true}
                    />
                )}
            </Grid>
        </Screen>
    );
}

export const ListInfo = ({ label, icon, xs }) => {
    return (
        <Grid xs={xs || 6} item>
            <ListItem sx={{ p: 0 }}>
                <ListItemIcon sx={{ mr: 1, minWidth: 24 }}>
                    <Iconify style={{ width: 24, height: 24 }} icon={icon} />
                </ListItemIcon>
                <ListItemText
                    primary={
                        <Typography color="text.secondary" component="span">
                            {label}
                        </Typography>
                    }
                />
            </ListItem>
        </Grid>
    );
};
